import type { FieldConfig, FieldDataNameProperties, VariableConfig } from "../fields/types";
import { isEmpty, isNil } from "lodash-es";
import type { GenericSchema } from "valibot";
import { never, object, pick, safeParse } from "valibot";
import { errorTranslation } from "./validation/base";
import FieldId from "../fields/FieldId";
import { equalsCaseInsensitive } from "./stringUtil";
import type { Issue } from "../state/validation";
import { keyFromSelector } from "i18next";

type FieldConfigWithDataName = FieldConfig<FieldDataNameProperties>;

const fieldDataNameErrorSchema = object({
  data_name: never(errorTranslation(keyFromSelector(($) => $.VALIDATION_DATA_NAME_UNIQUE))),
}) satisfies GenericSchema<FieldDataNameProperties>;

const variableDataNameErrorSchema = object({
  dataName: never(errorTranslation(keyFromSelector(($) => $.VALIDATION_DATA_NAME_UNIQUE))),
}) satisfies GenericSchema<Pick<VariableConfig, "dataName">>;

const variableNameErrorSchema = object({
  name: never(errorTranslation(keyFromSelector(($) => $.VALIDATION_VARIABLE_NAME_UNIQUE))),
}) satisfies GenericSchema<Pick<VariableConfig, "name">>;

const hasDataName = (field: FieldConfigWithDataName): field is FieldConfig<Required<FieldDataNameProperties>> =>
  !isNil(field.properties.data_name);

const isFieldConfig = (config: FieldConfigWithDataName | VariableConfig): config is FieldConfigWithDataName =>
  config.uid instanceof FieldId;

export const isFieldDataNameDuplicated = (
  curr: FieldConfigWithDataName,
  fieldDataNameMap: Map<string, FieldConfig[]>,
  variableDataNameMap: Map<string, VariableConfig[]>,
) => {
  if (!hasDataName(curr)) {
    return false;
  }

  const dataName = curr.properties.data_name.toLowerCase();
  const scopeKey = `${curr.scope.toString()}:${dataName}`;

  // Check for duplicates with variables
  const conflictingVariables = variableDataNameMap.get(dataName) ?? [];
  if (conflictingVariables.length > 0) {
    return true;
  }

  // Check for duplicates within scope
  const fieldsInScope = fieldDataNameMap.get(scopeKey) ?? [];
  return fieldsInScope.some((field) => !field.uid.equals(curr.uid));
};

export const isVariableDataNameDuplicated = (
  curr: VariableConfig,
  fieldDataNameMap: Map<string, FieldConfig[]>,
  variableDataNameMap: Map<string, VariableConfig[]>,
) => {
  if (isNil(curr.dataName)) {
    return false;
  }

  const dataName = curr.dataName.toLowerCase();

  // Check for duplicates with variables
  const conflictingVariables = variableDataNameMap.get(dataName) ?? [];
  if (conflictingVariables.some((variable) => !variable.uid.equals(curr.uid))) {
    return true;
  }

  // Check for duplicates with fields
  // Need to check all scopes for field conflicts
  for (const [key, fields] of fieldDataNameMap.entries()) {
    if (key.endsWith(`:${dataName}`) && !isEmpty(fields)) {
      return true;
    }
  }

  return false;
};

export const getDataNameIssues = (
  curr: FieldConfigWithDataName | VariableConfig,
  fieldDataNameMap: Map<string, FieldConfig[]>,
  variableDataNameMap: Map<string, VariableConfig[]>,
) => {
  if (isFieldConfig(curr)) {
    return isFieldDataNameDuplicated(curr, fieldDataNameMap, variableDataNameMap)
      ? (safeParse(fieldDataNameErrorSchema, curr.properties).issues ?? [])
      : [];
  }

  return isVariableDataNameDuplicated(curr, fieldDataNameMap, variableDataNameMap)
    ? (safeParse(variableDataNameErrorSchema, curr).issues ?? [])
    : [];
};

export const getVariableNameIssues = (curr: VariableConfig, variables: VariableConfig[]) =>
  variables
    .filter((variable) => !variable.uid.equals(curr.uid))
    .some((variable) => equalsCaseInsensitive(variable.name, curr.name))
    ? (safeParse(variableNameErrorSchema, curr).issues ?? [])
    : [];

export const pickSchema = <const TSchema extends Parameters<typeof pick>[0], TKey extends keyof TSchema["entries"]>(
  schema: TSchema,
  key: TKey,
) =>
  (
    pick(schema, [key]) as {
      entries: Pick<TSchema["entries"], TKey>;
    }
  ).entries[key];

export const findError = (issues: Issue[] | undefined | null, key: string) => {
  if (isNil(issues) || isEmpty(issues)) {
    return undefined;
  }
  return issues.find((issue) => (issue.path ?? []).some((p) => p.key === key))?.message;
};
